/**
 * Faucet spout centrelines — PURE 2D path helpers shared by the 3D tube
 * sweep (`faucetTube.js`) and the elevation outline (`FaucetSchematicParts`).
 *
 * A path is `{ start: [u, v], segs: [...] }` in the side plane (u = reach,
 * v = up), with two segment kinds:
 *   { kind: 'line', to: [u, v] }
 *   { kind: 'arc', c: [u, v], r, a0, a1 }   angles in radians, counter-
 *                                            clockwise positive in (u, v)
 *
 * Both consumers read the SAME segments: the tube sweeps a THREE curve per
 * segment, the schematic offsets the sampled polyline by the tube radius. A
 * spout can therefore not bend differently on paper and in 3D.
 */

export const ARC_SAMPLES = 12;

/** Point on an arc segment at angle `a`. */
export function arcPoint(seg, a) {
  return [seg.c[0] + seg.r * Math.cos(a), seg.c[1] + seg.r * Math.sin(a)];
}

/** Where a segment ends. */
export function segEnd(seg) {
  return seg.kind === 'arc' ? arcPoint(seg, seg.a1) : seg.to;
}

/** Where the whole path ends. */
export function pathEnd(path) {
  return path.segs.length
    ? segEnd(path.segs[path.segs.length - 1])
    : path.start;
}

/**
 * Sample the centreline into a polyline: line segments contribute their end
 * point, arcs `ARC_SAMPLES` points (their end included).
 */
export function samplePath(path, perArc = ARC_SAMPLES) {
  const pts = [path.start];
  for (const seg of path.segs) {
    if (seg.kind === 'line') {
      pts.push(seg.to);
      continue;
    }
    for (let i = 1; i <= perArc; i += 1) {
      pts.push(arcPoint(seg, seg.a0 + ((seg.a1 - seg.a0) * i) / perArc));
    }
  }
  return pts;
}

/**
 * Closed outline of a tube of radius `r` swept along the path: the left
 * offset forward, a flat cap, the right offset back, a flat cap. Vertex
 * normals are the mitred average of the adjoining segment normals, which is
 * exact on straight runs and within a fraction of a percent on the sampled
 * arcs. Returns an array of [u, v] points.
 */
export function offsetOutline(path, r, perArc = ARC_SAMPLES) {
  const pts = samplePath(path, perArc);
  const n = pts.length;
  const normals = [];
  for (let i = 0; i < n; i += 1) {
    const prev = pts[Math.max(0, i - 1)];
    const next = pts[Math.min(n - 1, i + 1)];
    const dirs = [];
    if (i > 0) dirs.push(unit(sub(pts[i], prev)));
    if (i < n - 1) dirs.push(unit(sub(next, pts[i])));
    const nIn = dirs.map(([du, dv]) => [-dv, du]);
    let nu = nIn.reduce((acc, [x]) => acc + x, 0) / nIn.length;
    let nv = nIn.reduce((acc, [, y]) => acc + y, 0) / nIn.length;
    // Mitre: the averaged normal shrinks by cos(half-angle); scale it back,
    // capped so a sharp joint cannot spike.
    const len = Math.hypot(nu, nv) || 1;
    const mitre = Math.min(1 / len, 2);
    normals.push([(nu / len) * mitre, (nv / len) * mitre]);
  }
  const left = pts.map((p, i) => add(p, scale(normals[i], r)));
  const right = pts.map((p, i) => sub(p, scale(normals[i], r))).reverse();
  return [...left, ...right];
}

/**
 * Round a metre coordinate for an SVG attribute. 0.1 µm is far below anything
 * a stroke can show, and it removes the last-ulp differences between
 * platforms' `Math.cos` / `Math.sin` (macOS vs Linux libm) that otherwise
 * make path snapshots flap between a dev machine and CI.
 */
export function svgNum(value) {
  return Math.round(value * 1e7) / 1e7;
}

/**
 * Map an outline (or any [u, v] polygon) to an SVG path `d` in a viewBox
 * whose y axis points DOWN: x = u + uOffset, y = vTop - v.
 */
export function polygonToSvgPath(points, { uOffset = 0, vTop }) {
  return (
    points
      .map(
        ([u, v], i) =>
          `${i === 0 ? 'M' : 'L'} ${svgNum(u + uOffset)} ${svgNum(vTop - v)}`
      )
      .join(' ') + ' Z'
  );
}

/** Extents of a polygon: { minU, maxU, minV, maxV }. */
export function polygonExtents(points) {
  return points.reduce(
    (e, [u, v]) => ({
      minU: Math.min(e.minU, u),
      maxU: Math.max(e.maxU, u),
      minV: Math.min(e.minV, v),
      maxV: Math.max(e.maxV, v),
    }),
    { minU: Infinity, maxU: -Infinity, minV: Infinity, maxV: -Infinity }
  );
}

const sub = (a, b) => [a[0] - b[0], a[1] - b[1]];
const add = (a, b) => [a[0] + b[0], a[1] + b[1]];
const scale = (a, k) => [a[0] * k, a[1] * k];
function unit(a) {
  const l = Math.hypot(a[0], a[1]) || 1;
  return [a[0] / l, a[1] / l];
}
